import { useCallback, useMemo } from 'react';

import { useChatConversationManager, type ChatConversationStateOptions } from './chat';
import { GatewaySessionNotReadyError } from '../errors/gatewaySessionErrors';
import { ConversationSession } from '../types/gateway-chat-types';

interface UseGatewayConversationManagerArgs {
  connectToConversation: (conversationId: string) => Promise<void>;
  gatewayApiBase: string;
  ensureSession: () => Promise<boolean>;
  authenticatedFetch: (url: string, options?: RequestInit) => Promise<Response>;
  config?: GatewayConversationManagerConfig;
}
const sortByRecentActivity = (a: ConversationSession, b: ConversationSession) => {
  const aTime = new Date(a.last_activity ?? a.created_at ?? 0).getTime();
  const bTime = new Date(b.last_activity ?? b.created_at ?? 0).getTime();
  return bTime - aTime;
};

const defaultGenerateConversationName = (index: number) => `Chat ${index + 1}`;

interface GatewayConversationContext {
  gatewayApiBase: string;
  ensureSession: () => Promise<boolean>;
  authenticatedFetch: (url: string, options?: RequestInit) => Promise<Response>;
}

const buildQueryString = (params?: Record<string, string | undefined>): string => {
  if (!params) {
    return '';
  }

  const entries = Object.entries(params).filter(([, value]) => value !== undefined);
  if (!entries.length) {
    return '';
  }

  const query = entries
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value ?? '')}`)
    .join('&');

  return `?${query}`;
};

export interface GatewayConversationManagerConfig {
  fetchConversations?: (context: GatewayConversationContext) => Promise<ConversationSession[]>;
  createConversation?: (
    context: GatewayConversationContext & { conversationName: string }
  ) => Promise<ConversationSession>;
  renameConversation?: (
    context: GatewayConversationContext & {
      conversationId: string;
      conversationName: string;
    }
  ) => Promise<ConversationSession>;
  generateConversationName?: (index: number) => string;
  sortComparator?: (a: ConversationSession, b: ConversationSession) => number;
  getConversationId?: ChatConversationStateOptions<ConversationSession>['getConversationId'];
  getFetchQueryParams?: () => Record<string, string | undefined>;
  getCreateQueryParams?: (options: {
    conversationName: string;
  }) => Record<string, string | undefined>;
}

export const useGatewayConversationManager = ({
  gatewayApiBase,
  ensureSession,
  authenticatedFetch,
  connectToConversation,
  config,
}: UseGatewayConversationManagerArgs) => {
  const context = useMemo<GatewayConversationContext>(
    () => ({ gatewayApiBase, ensureSession, authenticatedFetch }),
    [authenticatedFetch, ensureSession, gatewayApiBase]
  );
  const ensureSessionOrThrow = useCallback(async () => {
    const sessionValid = await ensureSession();
    if (!sessionValid) {
      throw new GatewaySessionNotReadyError();
    }
  }, [ensureSession]);

  const fetchConversations = useCallback(async () => {
    if (config?.fetchConversations) {
      return config.fetchConversations(context);
    }
    await ensureSessionOrThrow();
    const queryString = buildQueryString(config?.getFetchQueryParams?.());
    const conversationsBase = `${gatewayApiBase}/conversations`;
    const response = await authenticatedFetch(
      queryString ? `${conversationsBase}${queryString}` : conversationsBase
    );
    if (!response.ok) {
      throw new Error(`Failed to load conversations: ${response.statusText}`);
    }

    return (await response.json()) as ConversationSession[];
  }, [
    authenticatedFetch,
    config?.fetchConversations,
    context,
    ensureSessionOrThrow,
    gatewayApiBase,
  ]);

  const createConversationRequest = useCallback(
    async (conversationName?: string) => {
      const effectiveName = conversationName ?? '';

      if (config?.createConversation) {
        return config.createConversation({ ...context, conversationName: effectiveName });
      }

      const queryParams = {
        conversation_name: effectiveName,
        ...(config?.getCreateQueryParams?.({ conversationName: effectiveName }) ?? {}),
      };
      await ensureSessionOrThrow();
      const response = await authenticatedFetch(
        `${gatewayApiBase}/conversations/create${buildQueryString(queryParams)}`,
        { method: 'POST' }
      );

      if (!response.ok) {
        throw new Error(`Failed to create conversation: ${response.statusText}`);
      }

      return (await response.json()) as ConversationSession;
    },
    [authenticatedFetch, config?.createConversation, context, ensureSessionOrThrow, gatewayApiBase]
  );

  const renameConversationRequest = useCallback(
    async (conversationId: string, conversationName: string) => {
      if (config?.renameConversation) {
        return config.renameConversation({
          ...context,
          conversationId,
          conversationName,
        });
      }
      await ensureSessionOrThrow();
      const response = await authenticatedFetch(
        `${gatewayApiBase}/conversations/${conversationId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ conversation_name: conversationName }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to rename conversation: ${response.statusText}`);
      }

      return (await response.json()) as ConversationSession;
    },
    [authenticatedFetch, config?.renameConversation, context, ensureSessionOrThrow, gatewayApiBase]
  );

  const sortComparator = config?.sortComparator ?? sortByRecentActivity;
  const getConversationId = config?.getConversationId;
  const generateConversationName =
    config?.generateConversationName ?? defaultGenerateConversationName;

  return useChatConversationManager<ConversationSession>({
    fetchConversations,
    createConversation: createConversationRequest,
    renameConversation: renameConversationRequest,
    onConversationActivated: (id) => connectToConversation(id),
    sortComparator,
    ...(getConversationId ? { getConversationId } : {}),
    generateConversationName,
  });
};
